import { useMemo } from 'react'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import styled from 'styled-components'
import { Stats } from 'src/types/Affiliate'
import { formatCents, formatDay } from 'src/utils/affiliateFormat'
import { Table, TableWrap } from './ui'

// Validated against the dark card surface (dataviz validate_palette.js: lightness, CVD, contrast all pass).
const EARNED = '#e8508a'
const GRID = 'rgba(255, 255, 255, 0.08)'
const AXIS_TEXT = 'rgba(255, 255, 255, 0.55)'

type Point = { t: number; day: string; total: number; earned: number }

const BUCKET_LABEL = { day: 'That day', week: 'That week', month: 'That month' }

const toTime = (day: string) => new Date(`${day}T00:00:00Z`).getTime()

const compactDollars = (cents: number) => {
    const dollars = cents / 100
    if (Math.abs(dollars) >= 1000) return `$${(dollars / 1000).toFixed(dollars % 1000 === 0 ? 0 : 1)}k`
    return `$${Math.round(dollars)}`
}

// Keeps an all-zero range from labelling every gridline "$0"
const MIN_DOMAIN_CENTS = 2000

// Approximate width of a 12px axis label, used to size the y-axis to its longest tick
const TICK_CHAR_PX = 7
const TICK_GAP_PX = 12

// Y-axis labels start at the card's content edge instead of hanging off the end of a fixed-width gutter
const LeftTick = ({ y, payload }: any) => (
    <text x={0} y={y} dy={4} textAnchor="start" fill={AXIS_TEXT} fontSize={12}>
        {compactDollars(payload.value)}
    </text>
)

const TooltipBox = styled.div`
    background: #1d1828;
    border: 1px solid rgba(255, 255, 255, 0.12);
    border-radius: 10px;
    padding: 8px 12px;
    font-size: 13px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.35);

    p + p {
        margin-top: 2px;
    }
`

const ChartTooltip = ({ active, payload, bucket }: any) => {
    if (!active || !payload || payload.length === 0) return null
    const point: Point = payload[0].payload
    return (
        <TooltipBox>
            <p style={{ color: AXIS_TEXT }}>{formatDay(point.day)}</p>
            <p>Total earned: {formatCents(point.total)}</p>
            {point.earned !== 0 && (
                <p>
                    {BUCKET_LABEL[bucket as keyof typeof BUCKET_LABEL]}: {formatCents(point.earned)}
                </p>
            )}
        </TooltipBox>
    )
}

/** Running total earned over the selected range, or the same data as a table. */
const EarningsChart = ({ chart, showTable }: { chart: Stats['chart']; showTable: boolean }) => {
    const data = useMemo<Point[]>(
        () =>
            chart.history.map((h) => ({
                t: toTime(h.date),
                day: h.date,
                total: h.cumulativeCents,
                earned: h.earnedCents,
            })),
        [chart]
    )

    const maxCents = Math.max(MIN_DOMAIN_CENTS, ...data.map((d) => d.total))
    const axisWidth = compactDollars(maxCents).length * TICK_CHAR_PX + TICK_GAP_PX

    if (showTable) {
        return (
            <TableWrap>
                <Table>
                    <thead>
                        <tr>
                            <th>Date</th>
                            <th className="num">Earned</th>
                            <th className="num">Total</th>
                        </tr>
                    </thead>
                    <tbody>
                        {chart.history
                            .filter((h) => h.earnedCents !== 0)
                            .map((h) => (
                                <tr key={h.date}>
                                    <td>{formatDay(h.date)}</td>
                                    <td className="num">{formatCents(h.earnedCents)}</td>
                                    <td className="num">{formatCents(h.cumulativeCents)}</td>
                                </tr>
                            ))}
                    </tbody>
                </Table>
            </TableWrap>
        )
    }

    return (
        <div style={{ width: '100%', height: 280 }}>
            <ResponsiveContainer>
                <AreaChart data={data} margin={{ top: 10, right: 4, bottom: 0, left: 0 }}>
                    <defs>
                        <linearGradient id="earned-wash" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={EARNED} stopOpacity={0.18} />
                            <stop offset="100%" stopColor={EARNED} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid stroke={GRID} vertical={false} />
                    <XAxis
                        dataKey="t"
                        type="number"
                        scale="time"
                        domain={['dataMin', 'dataMax']}
                        tickFormatter={(t) =>
                            new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
                        }
                        tick={{ fill: AXIS_TEXT, fontSize: 12 }}
                        axisLine={{ stroke: GRID }}
                        tickLine={false}
                        minTickGap={32}
                    />
                    <YAxis
                        domain={[0, (dataMax: number) => Math.max(dataMax, MIN_DOMAIN_CENTS)]}
                        tick={<LeftTick />}
                        axisLine={false}
                        tickLine={false}
                        width={axisWidth}
                    />
                    <Tooltip
                        content={<ChartTooltip bucket={chart.bucket} />}
                        cursor={{ stroke: 'rgba(255,255,255,0.3)', strokeWidth: 1 }}
                    />
                    <Area
                        type="monotone"
                        dataKey="total"
                        stroke={EARNED}
                        strokeWidth={2}
                        fill="url(#earned-wash)"
                        activeDot={{ r: 5, stroke: '#1d1828', strokeWidth: 2 }}
                        isAnimationActive={false}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    )
}

export default EarningsChart
